import React, { memo, useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import CreatableSelect from 'react-select/creatable';
import { useFormik } from 'formik';
import {
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from 'reactstrap';

import { ReactComponent as CloseButtonSvg } from 'assets/images/location/close-button-svg.svg';
import Button from 'components/App/Button';

import styles from './ModalCreateVoucher.module.scss';
import {
  formatVoucherInput,
  INVALID_CODE_MESSAGE,
  isValidVoucherCode,
  normalizeCode,
  splitCodes,
  toSelectOptions,
  voucherCodesValidationSchema,
} from './voucherCodeValidate';

const selectStyles = {
  control: (provided, state) => ({
    ...provided,
    boxShadow: state.isFocused ? 'var(--bookese-blue-100)' : '',
    borderColor: state.isFocused
      ? 'var(--bookese-blue-100) !important'
      : 'var(--bookese-gray-200)',
    fontSize: '13px',
    minHeight: '120px',
    height: 'fit-content',
    cursor: 'text',
  }),
  input: (provided) => ({
    ...provided,
    fontSize: '13px',
  }),
  placeholder: (provided) => ({
    ...provided,
    fontSize: '13px',
    color: 'var(--bookese-gray-300)',
  }),
};

function ModalCreateVoucher(props) {
  const {
    isOpen,
    isLoadingConfirm,
    onCloseModal,
    onConfirm,
  } = props;

  const [inputValue, setInputValue] = useState('');
  const [inputError, setInputError] = useState('');

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      codes: [],
    },
    validationSchema: voucherCodesValidationSchema,
    onSubmit: async (values, { resetForm }) => {
      const codes = values.codes.map((item) => item.value);

      try {
        await onConfirm?.(codes);
        setInputValue('');
        setInputError('');
        resetForm();
        onCloseModal();
      } catch {
        // Giữ modal mở khi API lỗi
      }
    },
  });

  const resetInputState = useCallback(() => {
    setInputValue('');
    setInputError('');
  }, []);

  const handleClose = useCallback(() => {
    resetInputState();
    validation.resetForm();
    onCloseModal();
  }, [onCloseModal, resetInputState, validation]);

  const mergeCodes = useCallback((rawCodes) => {
    const existingCodes = validation.values.codes.map((item) => item.value);
    const nextCodes = [...existingCodes];
    let hasInvalid = false;

    rawCodes.forEach((rawCode) => {
      const code = normalizeCode(rawCode);

      if (!code) return;

      if (!isValidVoucherCode(code)) {
        hasInvalid = true;
        return;
      }

      if (!nextCodes.includes(code)) {
        nextCodes.push(code);
      }
    });

    validation.setFieldValue('codes', toSelectOptions(nextCodes), true);
    validation.setFieldTouched('codes', true, false);

    if (hasInvalid) {
      setInputError(INVALID_CODE_MESSAGE);
      return false;
    }

    setInputError('');
    return true;
  }, [validation]);

  const addCodesFromText = useCallback((text) => {
    const parsedCodes = splitCodes(text);
    if (!parsedCodes.length) return;

    mergeCodes(parsedCodes);
    setInputValue('');
  }, [mergeCodes]);

  const handleChangeCodes = useCallback((selectedOptions) => {
    validation.setFieldValue('codes', selectedOptions || [], true);
    validation.setFieldTouched('codes', true, false);
    setInputError('');
  }, [validation]);

  const handleInputChange = useCallback((value, { action }) => {
    if (action === 'input-change') {
      setInputValue(formatVoucherInput(value));
      if (inputError) setInputError('');
    }
  }, [inputError]);

  const handleKeyDown = useCallback((event) => {
    if (!inputValue) return;

    if (event.key === 'Enter' || event.key === ',' || event.key === ' ') {
      event.preventDefault();
      addCodesFromText(inputValue);
    }
  }, [addCodesFromText, inputValue]);

  const handleBlur = useCallback(() => {
    if (inputValue.trim()) {
      addCodesFromText(inputValue);
    }
  }, [addCodesFromText, inputValue]);

  const handleCreateOption = useCallback((value) => {
    addCodesFromText(value);
  }, [addCodesFromText]);

  const CloseButton = useMemo(
    () => (
      <button
        className={styles.ButtonClose}
        onClick={handleClose}
        type="button"
        aria-label="Đóng"
      >
        <CloseButtonSvg />
      </button>
    ),
    [handleClose],
  );

  const codesCount = validation.values.codes.length;
  const codesError = validation.touched.codes && validation.errors.codes
    ? validation.errors.codes
    : '';

  return (
    <Modal
      fade
      isOpen={isOpen}
      toggle={handleClose}
      className={styles.Modal}
      centered
    >
      <form onSubmit={validation.handleSubmit}>
        <div className={styles.ModalHeaderWrapper}>
          <h2 className={styles.ModalHeaderTitle}>Tạo voucher</h2>

          <ModalHeader
            toggle={handleClose}
            close={CloseButton}
            className={styles.ModalHeader}
          />
        </div>

        <ModalBody className={styles.ModalBody}>
          <div className={styles.FormField}>
            <Label className={styles.FormFieldLabel} htmlFor="voucherCodes">
              Nhập mã voucher
            </Label>

            <CreatableSelect
              inputId="voucherCodes"
              className={`basic-multi-select ${styles.CodesSelect}`}
              classNamePrefix="select2-selection"
              isMulti
              menuIsOpen={false}
              components={{ DropdownIndicator: null, IndicatorSeparator: null }}
              placeholder="Nhập mã voucher (3–20 ký tự)"
              value={validation.values.codes}
              inputValue={inputValue}
              onChange={handleChangeCodes}
              onInputChange={handleInputChange}
              onKeyDown={handleKeyDown}
              onBlur={handleBlur}
              onCreateOption={handleCreateOption}
              formatCreateLabel={() => null}
              styles={selectStyles}
              isClearable={false}
              isDisabled={isLoadingConfirm}
            />

            <p className={styles.FormHint}>
              Nhập một hoặc nhiều mã voucher, cách nhau bằng dấu phẩy, dấu cách hoặc nhấn Enter.
            </p>

            <p className={styles.FormHint}>
              Mỗi mã từ 3–20 ký tự, chỉ chữ cái và số (A–Z, a–z, 0–9), không ký tự đặc biệt.
            </p>

            {(inputError || codesError) && (
              <p className={styles.ErrorText}>{inputError || codesError}</p>
            )}

            <p className={styles.TotalCount}>
              Tổng số mã:
              <span className={styles.TotalCountBadge}>{codesCount}</span>
            </p>
          </div>
        </ModalBody>

        <ModalFooter className={styles.ModalFooter}>
          <div className="FooterButton">
            <div className="d-flex m-0 gap-2">
              <Button
                type="button"
                classes="Button Button__Cancel"
                onClick={handleClose}
                disabled={isLoadingConfirm}
              >
                Hủy
              </Button>

              <Button
                type="submit"
                classes="Button Button__Submit"
                disabled={isLoadingConfirm}
                loading={isLoadingConfirm}
              >
                Xác nhận
              </Button>
            </div>
          </div>
        </ModalFooter>
      </form>
    </Modal>
  );
}

ModalCreateVoucher.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  isLoadingConfirm: PropTypes.bool,

  onCloseModal: PropTypes.func.isRequired,
  onConfirm: PropTypes.func,
};

ModalCreateVoucher.defaultProps = {
  isLoadingConfirm: false,
  onConfirm: () => {},
};

export default memo(ModalCreateVoucher);
